import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { PortalOptionValueModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const CREATE_REQUEST = 'realbase/portalOptionValue/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/portalOptionValue/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/portalOptionValue/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/portalOptionValue/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/portalOptionValue/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/portalOptionValue/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/portalOptionValue/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/portalOptionValue/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/portalOptionValue/DELETE_FAILURE'

export type PortalOptionValueModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  updating: boolean,
}

// Initial State
const initialState: PortalOptionValueModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  updating: false,
}

// Actions
export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function createPortalOptionValue(
  portalOptionValue: Partial<PortalOptionValueModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      portal_option_value: portalOptionValue,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/portal_option_values.json', options, config)
      .then(({ data }: { data: PortalOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PORTAL_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePortalOptionValue(
  portalOptionValue: UpdateParams<PortalOptionValueModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      portal_option_value: portalOptionValue,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/portal_option_values/${portalOptionValue.id}.json`, options, config)
      .then(({ data }: { data: PortalOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PORTAL_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePortalOptionValue(portalOptionValue: DeleteParams<PortalOptionValueModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/portal_option_values/${portalOptionValue.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PORTAL_OPTION_VALUE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PortalOptionValueModuleState = initialState,
  action: Action = { type: '' },
): PortalOptionValueModuleState {
  switch (action.type){
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
